import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { testsAPI } from '../services/api';

function TestSubmissions() {
    const { id } = useParams();
    const navigate = useNavigate();
    
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        loadSubmissions();
    }, [id]);

    const loadSubmissions = async () => {
        try {
            const response = await testsAPI.getSubmissions(id);
            setSubmissions(response.data);
        } catch (err) {
            setError('Fehler beim Laden der Abgaben');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (submissionId, studentName) => {
        setDeleteConfirm({ submissionId, studentName, step: 1 });
    };

    const handleDeleteConfirm = async (submissionId) => {
        setDeleting(true);
        setError('');

        try {
            await testsAPI.deleteSubmission(id, submissionId);
            setSubmissions(submissions.filter(s => s.id !== submissionId));
            setDeleteConfirm(null);
        } catch (err) {
            setError('Fehler beim Löschen der Abgabe');
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirm(null);
    };

    if (loading) {
        return <div style={styles.loading}>Lädt...</div>;
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1>Abgaben</h1>
                <div style={styles.headerButtons}>
                    <button onClick={() => navigate(`/test/${id}`)} style={styles.backButton}>
                        ← Test Details
                    </button>
                    <button onClick={() => navigate('/dashboard')} style={styles.backButton}>
                        ← Dashboard
                    </button>
                </div>
            </div>

            {error && <div style={styles.error}>{error}</div>}

            {submissions.length === 0 ? (
                <div style={styles.empty}>
                    <p>Noch keine Abgaben für diesen Test.</p>
                </div>
            ) : (
                <div style={styles.submissionsGrid}>
                    {submissions.map(submission => (
                        <div key={submission.id} style={styles.submissionCard}>
                            <h3>{submission.student_name}</h3>
                            <div style={styles.submissionInfo}>
                                <div>
                                    <strong>Abgegeben:</strong>
                                    <br />
                                    {new Date(submission.submitted_at).toLocaleString('de-DE')}
                                </div>
                                {submission.score !== null && (
                                    <div style={styles.score}>
                                        <strong>Punkte:</strong>
                                        <br />
                                        <span style={styles.scoreValue}>
                                            {submission.score} / {submission.max_score}
                                        </span>
                                    </div>
                                )}
                                <div>
                                    <strong>Antworten:</strong>
                                    <br />
                                    {submission.answer_count}
                                </div>
                            </div>

                            {deleteConfirm?.submissionId === submission.id ? (
                                <div style={styles.deleteConfirmBox}>
                                    <p style={styles.deleteWarning}>
                                        ⚠️ Möchtest du die Abgabe von <strong>{submission.student_name}</strong> wirklich löschen?
                                    </p>
                                    <p style={styles.deleteHint}>
                                        Der Schüler kann danach den Test erneut durchführen.
                                    </p>
                                    <div style={styles.deleteButtons}>
                                        <button 
                                            onClick={() => handleDeleteConfirm(submission.id)}
                                            disabled={deleting}
                                            style={styles.deleteConfirmButton}
                                        >
                                            {deleting ? 'Wird gelöscht...' : 'Ja, löschen'}
                                        </button>
                                        <button 
                                            onClick={handleDeleteCancel}
                                            disabled={deleting}
                                            style={styles.cancelButton}
                                        >
                                            Abbrechen
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div style={styles.cardActions}>
                                    <button 
                                        onClick={() => navigate(`/test/${id}/submissions/${submission.id}`)}
                                        style={styles.viewButton}
                                    >
                                        Details anzeigen
                                    </button>
                                    <button 
                                        onClick={() => handleDeleteClick(submission.id, submission.student_name)}
                                        style={styles.deleteButton}
                                    >
                                        🗑️ Löschen
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

const styles = {
    container: {
        padding: '2rem',
        maxWidth: '1200px',
        margin: '0 auto',
    },
    loading: {
        textAlign: 'center',
        padding: '3rem',
        fontSize: '1.2rem',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
    },
    headerButtons: {
        display: 'flex',
        gap: '0.5rem',
    },
    backButton: {
        padding: '0.5rem 1rem',
        backgroundColor: '#6c757d',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
    },
    error: {
        backgroundColor: '#fee',
        color: '#c33',
        padding: '1rem',
        borderRadius: '4px',
        marginBottom: '1rem',
    },
    empty: {
        textAlign: 'center',
        padding: '3rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
    },
    submissionsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '1.5rem',
    },
    submissionCard: {
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    submissionInfo: {
        margin: '1rem 0',
        padding: '1rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
    },
    score: {
        padding: '0.5rem',
        backgroundColor: '#d4edda',
        borderRadius: '4px',
    },
    scoreValue: {
        fontSize: '1.5rem',
        fontWeight: 'bold',
        color: '#28a745',
    },
    cardActions: {
        display: 'flex',
        gap: '0.5rem',
        marginTop: '1rem',
    },
    viewButton: {
        flex: 1,
        padding: '0.75rem',
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        fontSize: '1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
    },
    deleteButton: {
        padding: '0.75rem 1rem',
        backgroundColor: '#dc3545',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '0.9rem',
    },
    deleteConfirmBox: {
        marginTop: '1rem',
        padding: '1rem',
        backgroundColor: '#fff3cd',
        borderRadius: '4px',
        border: '2px solid #ffc107',
    },
    deleteWarning: {
        marginBottom: '0.5rem',
        color: '#856404',
        fontWeight: 'bold',
        textAlign: 'center',
    },
    deleteHint: {
        marginBottom: '1rem',
        color: '#856404',
        textAlign: 'center',
        fontSize: '0.9rem',
    },
    deleteButtons: {
        display: 'flex',
        gap: '0.5rem',
        justifyContent: 'center',
    },
    deleteConfirmButton: {
        padding: '0.5rem 1rem',
        backgroundColor: '#dc3545',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontWeight: 'bold',
    },
    cancelButton: {
        padding: '0.5rem 1rem',
        backgroundColor: '#6c757d',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
    },
};

export default TestSubmissions;
